import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import type { PodTemplate } from "@/services/api/agentic/types";
import {
  getPodTemplates,
  pinPodTemplate,
  unpinPodTemplate,
  getPinnedPodTemplateIds,
  getPinnedPodTemplates,
} from "@/services/api/agentic";
import { useAuth } from "./AuthContext";

interface PodListContextType {
  pods: PodTemplate[];
  loading: boolean;
  error: string | null;
  pinnedPods: string[];
  setPods: (pods: PodTemplate[]) => void;
  removePod: (podId: string) => void;
  addPod: (pod: PodTemplate) => void;
  updatePod: (podId: string, updatedPod: PodTemplate) => void;
  refreshPods: () => Promise<void>;
  togglePodPin: (podId: string) => Promise<void>;
  refreshPinnedPods: () => Promise<void>;
}

const PodListContext = createContext<PodListContextType | undefined>(undefined);

export function PodListProvider({ children }: { children: ReactNode }) {
  const [pods, setPods] = useState<PodTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pinnedPods, setPinnedPods] = useState<string[]>([]);
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

  // Load pinned pods from API on mount
  const refreshPinnedPods = useCallback(async () => {
    if (isAuthLoading || !isAuthenticated) {
      return;
    }

    try {
      const pinnedIds = await getPinnedPodTemplateIds();
      setPinnedPods(pinnedIds);
    } catch (err) {
      console.error("Error fetching pinned pods:", err);
      // Fallback to empty array if API fails
      setPinnedPods([]);
    }
  }, [isAuthenticated, isAuthLoading]);

  useEffect(() => {
    refreshPinnedPods();
  }, [refreshPinnedPods]);

  const togglePodPin = useCallback(
    async (podId: string) => {
      try {
        if (pinnedPods.includes(podId)) {
          await unpinPodTemplate(podId);
          setPinnedPods((prev) => prev.filter((id) => id !== podId));
        } else {
          await pinPodTemplate(podId);
          setPinnedPods((prev) => [...prev, podId]);
        }
      } catch (err) {
        console.error("Error toggling pod pin:", err);
        // Optionally show an error notification to the user
      }
    },
    [pinnedPods]
  );

  const refreshPods = useCallback(async () => {
    if (isAuthLoading || !isAuthenticated) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const podsData = await getPodTemplates();
      // Handle both paginated and non-paginated responses
      const podsArray = Array.isArray(podsData)
        ? podsData
        : podsData.results || [];
      setPods(podsArray);
    } catch (err) {
      console.error("Error fetching pod templates:", err);
      setError("Failed to load pod templates");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, isAuthLoading]);

  useEffect(() => {
    refreshPods();
  }, [refreshPods]);

  const removePod = useCallback((podId: string) => {
    setPods((prev) => prev.filter((pod) => pod._id !== podId));
    // Also remove from pinned pods if it was pinned
    setPinnedPods((prev) => prev.filter((id) => id !== podId));
  }, []);

  const addPod = useCallback((pod: PodTemplate) => {
    setPods((prev) => {
      const newPods = [...prev, pod];
      return newPods;
    });
  }, []);

  const updatePod = useCallback((podId: string, updatedPod: PodTemplate) => {
    setPods((prev) => {
      const newPods = prev.map((pod) => (pod._id === podId ? updatedPod : pod));
      return newPods;
    });
  }, []);

  return (
    <PodListContext.Provider
      value={{
        pods,
        loading,
        error,
        pinnedPods,
        setPods,
        removePod,
        addPod,
        updatePod,
        refreshPods,
        togglePodPin,
        refreshPinnedPods,
      }}
    >
      {children}
    </PodListContext.Provider>
  );
}

export function usePodList() {
  const context = useContext(PodListContext);
  if (context === undefined) {
    throw new Error("usePodList must be used within a PodListProvider");
  }
  return context;
}
